import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Flex,
  IconButton,
  Image,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { CircleCheck, CircleAlert, Link2 } from "lucide-react";
import { getGovbrConnectionScreenshot } from "../../_services/credential.service";
import { IGovbrConnectionAttempt } from "../../_services/interface/credential.interface";
import { emCurso, erroNaoVisto, useGovbrConnections } from "../../contexts/GovbrConnectionContext";
import { useAppData, useConfigGuard } from "../../contexts/AppContext";
import { mensagemDaValidacaoGovbr } from "../../pages/Credentials/components/govbrValidationMessages";
import { topNavLayers } from "./topNavLayers";
import { TOPNAV_ICON_BUTTON_PROPS } from "./constants";

/**
 * O que a pessoa lê para cada etapa. Só o que o servidor de fato reporta — nada de etapa inventada.
 *
 * ⚠️ As retentativas são SILENCIOSAS (decisão de 13/10/2026): entre uma volta e outra o popup
 * continua dizendo "conectando", sem "tentativa 2 de 4".
 */
export const ETAPAS_GOVBR: Record<string, string> = {
  na_fila: "Aguardando para conectar",
  abrindo: "Abrindo o gov.br",
  cpf: "Informando o CPF",
  senha: "Informando a senha",
  mfa_sugerido: "Passando pela tela de segurança do gov.br (até 60 s)",
  certificado: "Apresentando o certificado digital",
  selando: "Guardando a conexão com segurança",
  aguardando_nova_tentativa: "Conectando ao gov.br",
};

export const textoDaEtapa = (stage: string | null) => (stage && ETAPAS_GOVBR[stage]) || "Conectando ao gov.br";

/** Print da tela em que o login parou — buscado só quando a pessoa pede (contém dados do titular). */
const PrintDoErro = ({ guidClient, attempt }: { guidClient: string; attempt: IGovbrConnectionAttempt }) => {
  const [estado, setEstado] = useState<"fechado" | "carregando" | "erro" | string>("fechado");

  const abrir = async () => {
    setEstado("carregando");
    const { status, response } = await getGovbrConnectionScreenshot(guidClient, attempt.id);
    const dataUrl = (response as { data_url?: string } | undefined)?.data_url;
    setEstado(status === 200 && dataUrl?.startsWith("data:image/png") ? dataUrl : "erro");
  };

  if (estado === "fechado") {
    return (
      <Button variant="link" size="xs" fontSize="12px" color="#4B4EFF" alignSelf="flex-start" onClick={abrir}>
        Ver tela do erro
      </Button>
    );
  }

  if (estado === "carregando") return <Spinner size="xs" color="#375CA9" />;

  if (estado === "erro") {
    return (
      <Text fontSize="12px" color="#77777B">
        A imagem da tela não está mais disponível.
      </Text>
    );
  }

  // Sem link para abrir em outra aba: o Chrome bloqueia navegação de topo para `data:` URL.
  return (
    <Box borderWidth="1px" borderColor="#E2E8F0" borderRadius="6px" overflow="hidden">
      <Image src={estado} alt="Tela do gov.br no momento do erro" w="100%" />
    </Box>
  );
};

const LinhaDaConexao = ({ guidClient, attempt }: { guidClient: string; attempt: IGovbrConnectionAttempt }) => {
  const ativa = emCurso(attempt);
  const sucesso = attempt.status === "succeeded";
  const mensagem = mensagemDaValidacaoGovbr(sucesso ? "reconectada" : attempt.outcome);

  return (
    <Flex direction="column" gap="4px" px="14px" py="10px">
      <Text fontSize="13px" fontWeight="600" color="#303033" noOfLines={1}>
        {attempt.label}
      </Text>

      {ativa ? (
        <Flex align="center" gap="8px">
          <Spinner size="xs" color="#375CA9" />
          <Text fontSize="12px" color="#46464A">
            {textoDaEtapa(attempt.stage)}
          </Text>
        </Flex>
      ) : (
        <Flex align="flex-start" gap="8px">
          <Box pt="1px" color={sucesso ? "#16A34A" : "#B42318"}>
            {sucesso ? <CircleCheck size={14} /> : <CircleAlert size={14} />}
          </Box>
          <Flex direction="column" gap="2px">
            <Text fontSize="12px" fontWeight="600" color="#303033">
              {mensagem.titulo}
            </Text>
            {sucesso ? null : (
              <Text fontSize="12px" color="#77777B" lineHeight="17px">
                {mensagem.detalhe}
              </Text>
            )}
          </Flex>
        </Flex>
      )}

      {attempt.status === "failed" && attempt.has_screenshot ? (
        <PrintDoErro guidClient={guidClient} attempt={attempt} />
      ) : null}
    </Flex>
  );
};

/**
 * GEN-3754 — ícone da conexão gov.br em segundo plano, na barra superior, ao lado da busca.
 *
 * Clone estrutural do `AlertsBell` (mesmo `IconButton` ghost 30×30, mesmo `Popover`, mesmo badge):
 * é um vizinho dele na mesma faixa e tem de ser indistinguível em forma.
 *
 * Só existe enquanto há o que mostrar: conexão em curso (anel girando), erro ainda não visto
 * (badge) ou sucesso dos últimos segundos. Abrir o popup marca os erros como vistos.
 */
export const GovbrConnectionIndicator = () => {
  const { attempts, markSeen } = useGovbrConnections();
  const { guid_client } = useAppData();
  const { requestNavigation } = useConfigGuard();
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (attempts.length === 0) return null;

  const ativas = attempts.some(emCurso);
  const comErro = attempts.some(erroNaoVisto);
  const algumaFalhou = attempts.some((a) => a.status === "failed");

  // "concluída" só quando TUDO o que está no ícone deu certo: um erro já visto continua listado
  // no popup, e o rótulo não pode afirmar sucesso por cima dele.
  const rotulo = ativas
    ? "Conexão gov.br em andamento"
    : algumaFalhou
    ? "Conexão gov.br com erro"
    : "Conexão gov.br concluída";

  const abrir = () => {
    onOpen();
    void markSeen();
  };

  const irParaCredenciais = () => {
    onClose();
    requestNavigation("/credenciais");
  };

  return (
    <Popover isOpen={isOpen} onOpen={abrir} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Box position="relative" display="inline-flex">
          <Tooltip label={rotulo} openDelay={600}>
            <IconButton
              aria-label={rotulo}
              {...TOPNAV_ICON_BUTTON_PROPS}
              icon={
                <Box position="relative" display="inline-flex" alignItems="center" justifyContent="center">
                  {ativas ? (
                    // Anel indeterminado em volta do ícone: "conexão + loading" sem trocar o ícone,
                    // para a pessoa reconhecer o mesmo botão em curso e depois do desfecho.
                    <CircularProgress
                      isIndeterminate
                      size="26px"
                      thickness="6px"
                      color="#375CA9"
                      trackColor="transparent"
                      position="absolute"
                      aria-hidden="true"
                    />
                  ) : null}
                  <Link2 size={15} />
                </Box>
              }
            />
          </Tooltip>
          {comErro ? (
            <Flex
              // Mesmo badge do `AlertsBell`; o aria-label do botão já diz que há erro.
              aria-hidden="true"
              position="absolute"
              top="-4px"
              right="-4px"
              minW="18px"
              h="18px"
              px="4px"
              borderRadius="9px"
              backgroundColor="#FEE2E2"
              color="#B42318"
              borderWidth="1px"
              borderColor="#FECDCA"
              fontSize="10px"
              fontWeight="500"
              align="center"
              justify="center"
              pointerEvents="none"
            >
              !
            </Flex>
          ) : null}
        </Box>
      </PopoverTrigger>

      <PopoverContent zIndex={topNavLayers.panel} w="380px" boxShadow="lg" _focusVisible={{ outline: "none" }}>
        <PopoverHeader borderBottomWidth="1px" py="10px">
          <Text fontSize="14px" fontWeight="600" color="#303033">
            Conexão gov.br
          </Text>
        </PopoverHeader>

        <PopoverBody p="0" maxH="420px" overflowY="auto" className="scrollbarsidebar">
          {attempts.map((attempt, index) => (
            <Box key={attempt.id}>
              {index > 0 ? <Divider /> : null}
              <LinhaDaConexao guidClient={guid_client} attempt={attempt} />
            </Box>
          ))}
        </PopoverBody>

        {algumaFalhou ? (
          <Flex borderTopWidth="1px" p="8px">
            <Button variant="ghost" size="sm" w="100%" fontSize="13px" onClick={irParaCredenciais}>
              Credenciais
            </Button>
          </Flex>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
